import { useEffect, useState, type FormEvent } from "react";
import { api, money } from "../../api";
import type { Donation, Fund, GivingReport, Person } from "../../../shared/types";
import { ErrorNote } from "../../ui";
import { formatDateKey, monthStartKey, todayKey } from "../calendar/dates";
import { PageFrame, messageOf } from "../calendar/chrome";

interface GivingBatch {
  id: string;
  name: string;
  committedAt: string | null;
  notes: string;
  createdAt: string;
}

interface GivingPayload {
  funds: Fund[];
  batches: GivingBatch[];
  donations: Donation[];
  provider: { configured: boolean; mode: string };
}

const METHODS = [
  ["cash", "Cash"],
  ["bank-transfer", "Bank transfer"],
  ["eftpos-offline", "EFTPOS, recorded offline"],
] as const;

function looksLikeCard(value: string): boolean {
  return /\d(?:[ -]?\d){12,18}/.test(value);
}

function dollarsToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(Number(trimmed) * 100);
  if (cents < 1 || cents > 10_000_000) return null;
  return cents;
}

function methodLabel(method: Donation["method"]): string {
  return METHODS.find((item) => item[0] === method)?.[1] ?? method;
}

export function GivingPage() {
  const [ledger, setLedger] = useState<GivingPayload | null>(null);
  const [report, setReport] = useState<GivingReport | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [from, setFrom] = useState(monthStartKey);
  const [to, setTo] = useState(todayKey);
  const [fundFilter, setFundFilter] = useState("");
  const [fundId, setFundId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [personId, setPersonId] = useState("");
  const [donor, setDonor] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<Donation["method"]>("cash");
  const [givenOn, setGivenOn] = useState(todayKey);
  const [memo, setMemo] = useState("");
  const [fundName, setFundName] = useState("");
  const [fundNotes, setFundNotes] = useState("");
  const [batchName, setBatchName] = useState("");
  const [batchNotes, setBatchNotes] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    setError("");
    const query = new URLSearchParams({ from, to });
    if (fundFilter) query.set("fundId", fundFilter);
    void Promise.all([
      api<GivingPayload>("/api/v1/giving"),
      api<GivingReport>(`/api/v1/giving/report?${query.toString()}`),
      api<Person[]>("/api/v1/people?limit=100").catch(() => [] as Person[]),
    ]).then(([nextLedger, nextReport, nextPeople]) => {
      if (!live) return;
      setLedger(nextLedger);
      setReport(nextReport);
      setPeople(nextPeople);
      setFundId((current) => current || nextLedger.funds.find((fund) => fund.active)?.id || "");
    }).catch((caught: unknown) => { if (live) setError(messageOf(caught)); })
      .finally(() => { if (live) setSaving(""); });
    return () => { live = false; };
  }, [reload, from, to, fundFilter]);

  const cents = dollarsToCents(amount);
  const activeFunds = ledger?.funds.filter((fund) => fund.active) ?? [];

  async function recordGift(event: FormEvent) {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!fundId) {
      setError("Choose a fund.");
      return;
    }
    if (!donor.trim()) {
      setError("Enter the donor name. A walk-up gift can use a description such as Walk-up offering.");
      return;
    }
    if (cents == null) {
      setError("Enter an amount from $0.01 to $100,000.00, such as 25 or 25.50.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(givenOn)) {
      setError("Choose the date of the gift.");
      return;
    }
    if (looksLikeCard(memo) || looksLikeCard(donor)) {
      setError("Do not store card numbers. Record the offline method only.");
      return;
    }
    setSaving("gift");
    try {
      await api<Donation>("/api/v1/giving/donations", {
        method: "POST",
        body: JSON.stringify({ fundId, batchId: batchId || null, personId: personId || null, donorName: donor.trim(), amountCents: cents, method, givenOn, memo: memo.trim() }),
      });
      setNotice(givenOn < from || givenOn > to ? "Saved on the ledger. That date is outside the report range, so widen the dates to see it." : "Saved on the offline ledger. Nothing was charged.");
      setDonor("");
      setAmount("");
      setMemo("");
      setPersonId("");
      setReload((value) => value + 1);
    } catch (caught) {
      setError(messageOf(caught));
      setSaving("");
    }
  }

  async function addFund(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!fundName.trim()) {
      setError("Name the fund.");
      return;
    }
    setSaving("fund");
    try {
      await api("/api/v1/giving/funds", { method: "POST", body: JSON.stringify({ name: fundName.trim(), description: fundNotes.trim() }) });
      setFundName("");
      setFundNotes("");
      setNotice("Fund added. Funds stay active — there is no archive route.");
      setReload((value) => value + 1);
    } catch (caught) {
      setError(messageOf(caught));
      setSaving("");
    }
  }

  async function addBatch(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!batchName.trim()) {
      setError("Name the batch.");
      return;
    }
    setSaving("batch");
    try {
      await api("/api/v1/giving/batches", { method: "POST", body: JSON.stringify({ name: batchName.trim(), notes: batchNotes.trim() }) });
      setBatchName("");
      setBatchNotes("");
      setNotice("Batch opened. Closing it against a bank is not available, so it stays a local grouping.");
      setReload((value) => value + 1);
    } catch (caught) {
      setError(messageOf(caught));
      setSaving("");
    }
  }

  async function downloadCsv() {
    setError("");
    setSaving("csv");
    try {
      const response = await fetch(`/api/v1/exports/donations.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, { credentials: "include" });
      const type = response.headers.get("content-type") ?? "";
      if (!response.ok || type.includes("json")) {
        const body = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(body?.error ?? "Export failed");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "mblive-giving.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setNotice("CSV downloaded for every fund in those dates. The on-screen fund filter is not applied to the file.");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setSaving("");
    }
  }

  const peak = Math.max(1, ...(report?.funds.map((fund) => fund.totalCents) ?? [1]));

  return (
    <PageFrame kicker="Operations" title="Giving" lede="A fictional offline ledger for Moksha Base. Card payments are not configured, and this screen never charges a card or stores a card number.">
      {error && <ErrorNote>{error}</ErrorNote>}
      {notice && <p className="ok-note" role="status">{notice}</p>}
      <div className="ribbon">
        <strong>Offline ledger only.</strong>
        <p>{ledger?.provider.configured ? "The server reports a payment provider. This screen still only records cash, bank transfer, and offline EFTPOS, because those are the methods the API accepts." : `Payments are not configured (${ledger?.provider.mode ?? "offline-ledger"}). Recorded gifts are local bookkeeping, not settled funds.`}</p>
      </div>
      {!ledger && !error ? <p>Loading the ledger…</p> : null}
      {ledger && (
        <div className="ops-split flip">
          <div className="stack">
            <article className="panel panel-pad stack">
              <div className="split">
                <h2>Report</h2>
                <strong>{report ? money(report.totalCents) : "—"}</strong>
              </div>
              <div className="cluster">
                <label className="field grow">From<input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
                <label className="field grow">To<input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
                <label className="field grow">Fund
                  <select value={fundFilter} onChange={(event) => setFundFilter(event.target.value)}>
                    <option value="">All funds</option>
                    {ledger.funds.map((fund) => <option key={fund.id} value={fund.id}>{fund.name}</option>)}
                  </select>
                </label>
              </div>
              <p className="faint">Changing the dates reloads the report. CSV export uses the dates and includes every fund.</p>
              <button className="btn" type="button" disabled={saving === "csv"} onClick={() => void downloadCsv()}>{saving === "csv" ? "Preparing…" : "Download CSV"}</button>
              {report && report.funds.length === 0 ? <p className="muted">No gifts in this range.</p> : report?.funds.map((fund) => (
                <div key={fund.fundId}>
                  <div className="split"><span>{fund.fundName}</span><span>{money(fund.totalCents)} · {fund.count}</span></div>
                  <div className="meter" aria-hidden="true"><span style={{ width: `${Math.round((fund.totalCents / peak) * 100)}%` }} /></div>
                </div>
              ))}
            </article>
            <article className="panel table-wrap">
              <div className="panel-pad"><h2>Gifts in the report</h2></div>
              <table className="data">
                <thead><tr><th>Date</th><th>Donor</th><th>Fund</th><th>Method</th><th>Amount</th><th>Batch</th></tr></thead>
                <tbody>
                  {(report?.donations ?? []).map((gift) => (
                    <tr key={gift.id}>
                      <td>{formatDateKey(gift.givenOn)}</td>
                      <td>{gift.donorName}{gift.memo ? <div className="faint">{gift.memo}</div> : null}</td>
                      <td>{gift.fundName}</td>
                      <td>{methodLabel(gift.method)}</td>
                      <td>{money(gift.amountCents)}</td>
                      <td>{gift.batchName ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="panel-pad faint">Gifts cannot be edited or deleted after they are recorded. Correct a mistake with another dated entry and a memo.</p>
            </article>
          </div>
          <div className="stack">
            <form className="panel panel-pad form-grid" onSubmit={(event) => void recordGift(event)}>
              <h2>Record a gift</h2>
              <label className="field">Fund
                <select value={fundId} onChange={(event) => setFundId(event.target.value)}>
                  {activeFunds.map((fund) => <option key={fund.id} value={fund.id}>{fund.name}</option>)}
                </select>
              </label>
              <label className="field">Batch
                <select value={batchId} onChange={(event) => setBatchId(event.target.value)}>
                  <option value="">No batch</option>
                  {ledger.batches.map((batch) => <option key={batch.id} value={batch.id}>{batch.name}</option>)}
                </select>
              </label>
              <label className="field">Linked person
                <select value={personId} onChange={(event) => {
                  const next = event.target.value;
                  setPersonId(next);
                  const person = people.find((item) => item.id === next);
                  if (person) setDonor(person.name);
                }}>
                  <option value="">No linked person</option>
                  {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                </select>
              </label>
              <label className="field">Donor name<input value={donor} onChange={(event) => setDonor(event.target.value)} maxLength={140} required /></label>
              <label className="field">Amount (NZD)<input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="25.00" required /></label>
              {cents != null ? <p className="faint">Records as {money(cents)}.</p> : null}
              <label className="field">Method
                <select value={method} onChange={(event) => setMethod(event.target.value as Donation["method"])}>
                  {METHODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="field">Date<input type="date" value={givenOn} onChange={(event) => setGivenOn(event.target.value)} required /></label>
              <label className="field">Memo<textarea value={memo} onChange={(event) => setMemo(event.target.value)} maxLength={400} placeholder="Envelope note. Do not enter a card number." /></label>
              <button className="btn primary" type="submit" disabled={saving === "gift"}>{saving === "gift" ? "Saving…" : "Record gift"}</button>
            </form>
            <form className="panel panel-pad form-grid" onSubmit={(event) => void addFund(event)}>
              <h2>New fund</h2>
              <label className="field">Name<input value={fundName} onChange={(event) => setFundName(event.target.value)} maxLength={80} /></label>
              <label className="field">Description<input value={fundNotes} onChange={(event) => setFundNotes(event.target.value)} maxLength={400} /></label>
              <button className="btn" type="submit" disabled={saving === "fund"}>{saving === "fund" ? "Saving…" : "Add fund"}</button>
            </form>
            <form className="panel panel-pad form-grid" onSubmit={(event) => void addBatch(event)}>
              <h2>New batch</h2>
              <p className="faint">A batch groups offline gifts. It is not a bank deposit.</p>
              <label className="field">Name<input value={batchName} onChange={(event) => setBatchName(event.target.value)} maxLength={120} /></label>
              <label className="field">Notes<input value={batchNotes} onChange={(event) => setBatchNotes(event.target.value)} maxLength={400} /></label>
              <button className="btn" type="submit" disabled={saving === "batch"}>{saving === "batch" ? "Saving…" : "Add batch"}</button>
              {ledger.batches.map((batch) => (
                <p key={batch.id} className="muted">{batch.name} · {batch.committedAt ? "Counted" : "Open"}{batch.notes ? ` · ${batch.notes}` : ""}</p>
              ))}
            </form>
          </div>
        </div>
      )}
    </PageFrame>
  );
}
